import React, { useEffect, useState } from "react";
import defaultUser from "../../assets/images/default-profile.svg";
import styles from "./index.module.scss";
import Loader from "../../common/components/Loader";
import TableWithPaginationComponent from "../../common/components/TableComponentWithPagination";
import { Avatar, Tooltip } from "@mui/material";
import search from '../../assets/images/dashboard-search.svg';
import { calculateAge, decryptData, modifyProfileUrl } from "../../utils/commonFunctions";
import { getCall } from "../../services/apiService";
import { getItemInLocalStorage } from "../../services/localStorage";
import { allSeekersListColumns } from "../../constants";
import clearIcon from "../../assets/images/close-icon.svg";
import { useNavigate } from "react-router-dom";
import { endPoints, INFINIPATH } from "../../constants/urlConstants";


const AllSeekersList: React.FC = () => {
  // State variables for managing data, pagination, loading, and search
  const [dataToShow, setDataToShow] = useState<unknown[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalAudience, setTotalAudience] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoader, setFilterLoader] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [finalsearchText, setFinalSearchText] = useState<string>("");
 const [searchValue, setSearchValue] = useState({ value: "", open: false });
  const navigate = useNavigate();
   // Function to fetch data from the API
  const fetchData = () => {
      setFilterLoader(true);
  
      const userId = getItemInLocalStorage("seekerDetails")?.id;

      getCall(
        `users?excludeUserAndMembers=${userId}&page=${currentPage}&size=${pageSize}&searchString=${finalsearchText}`, undefined, INFINIPATH)
        .then((res: unknown) => {
          if (res?.status === 200) {
            setLoading(false);
            const dataReq = res?.data?.data?.usersData;
            setDataToShow(dataReq);
            setTotalAudience(res?.data?.data?.totalUsersCount);
          } else {
            setLoading(false);
          }
        })
        .catch((err:unknown) => {
          alert("Error fetching data");
          setLoading(false);
        })
        .finally(() => {
          setFilterLoader(false);
        });
    
  };

// Fetch data whenever currentPage, pageSize, or finalsearchText changes
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize,finalsearchText]);

  // Handle page change in pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Handle page size change in pagination
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to the first page when page size changes
  };

  
  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (value.trim() === "") {
      setFinalSearchText(""); // Set finalSearchText to empty if input is empty
      setCurrentPage(1); // Reset to the first page when search input is empty
    }
  };

  // Get the appropriate items per page options based on total audience
  const getItemsPerPageOptions = () => {
    const options = [10, 50, 100];
    return options;
  };

  // Truncate long names for display
  const getDisplayName = (name: string, maxLength: number) => {
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  // Handle search action
  const handleSearch = (query: string) => {
    setFinalSearchText(query);
    setCurrentPage(1); // Reset to the first page when search is performed
  };


  const handleRowClick = (row: unknown) => {
     navigate(`${endPoints.seekerAnalytics}/${row.id}`);
  };
  // Render custom row for the table
  const renderCustomRow = (row: unknown) => (
    <tr
      key={row?.id ?? Math.random()} // Fallback to a random key if row.id is null/undefined
      className={styles.tableRow}
      onClick={() => handleRowClick(row)}
    >
    <td
        className={`${styles.tableCell} ${styles.userCell}`}
        style={{ maxWidth: "350px" }}
      >
        <Avatar
          alt={row?.firstName ?? "Unknown User"}
          src={
            (row?.profileUrl &&
            row?.profileUrl?.length > 0)
              ?  modifyProfileUrl(row?.profileUrl)
              : defaultUser
          }
          sx={{
            width: 40,
            height: 40,
            border: "1px solid #DDDDDD",
          }}
          data-testid="profile-avatar"
        />
        {row?.firstName?.length > 30 ? (
          <Tooltip title={row.firstName} arrow>
            <span>{getDisplayName(row.firstName, 30)}</span>
          </Tooltip>
        ) : (
          <span>{row?.firstName ?? "-"}</span>
        )}
      </td>
      <td className={styles.tableCell}>{row?.lastName ?? "-"}</td>
      <td className={styles.tableCell}>
      {decryptData(row?.encryptedEmail).length> 25 ? (
          <Tooltip title={decryptData(row.encryptedEmail)} arrow>
            <span>{getDisplayName(decryptData(row.encryptedEmail), 25)}</span>
          </Tooltip>
        ) : (
          <span>{decryptData(row?.encryptedEmail)?? "-"}</span>
        )}

        </td>
      <td
        className={`${styles.tableCell} ${styles.gender}`}
      >
        {row?.gender ? row?.gender[0]: "-"}
      </td>
      <td className={`${styles.tableCell} ${styles.gender}`}>
        {row?.dob ? calculateAge(row.dob) : "-"}
      </td>
      <td className={styles.tableCell}>
        {row?.countryCode ?? ""} {decryptData(row?.encryptedPhoneNumber) ?? "-"}
      </td>
      <td className={styles.tableCell} style={{ maxWidth: "350px" }}>
        {row?.address?.toLowerCase() === "other" ? (
          row?.otherAddress ? (
            `${row.address} - ${row.otherAddress}`.length > 24 ? (
              <Tooltip title={`${row.address} - ${row.otherAddress}`} arrow>
                <span>{getDisplayName(`${row.address} - ${row.otherAddress}`, 24)}</span>
              </Tooltip>
            ) : (
              `${row.address} - ${row.otherAddress}`
            )
          ) : (
            row.address
          )
        ) : row?.address?.length > 24 ? (
          <Tooltip title={row.address} arrow>
            <span>{getDisplayName(row.address, 24)}</span>
          </Tooltip>
        ) : (
          row.address ?? "-"
        )}
      </td>
    </tr>
  );
 
  // Render loader
  const renderLoader = () => (
    <div className={styles.loaderContainer}>
      <Loader type="small" data-testid="search-modal-loader" />
    </div>
  );

  return (
    <>
    {(!loading) ? ( <div
      className={styles.seekerListDashboard}
      data-testid="seeker-list-dashboard"
    >
      <div className={styles.header}>
        <div className={styles.totalAudienceContainer}>
          <span className={styles.totalAudienceLabel}>Total Seekers -</span>
          <span className={styles.totalAudienceCount}>{totalAudience}</span>
        </div>
          <div className={styles.searchContainer}>
                {searchValue.open && (
                  <div className={styles.search} data-testid="search-container">
                    <input
                      type="text"
                      className={styles.searchInput}
                      autoFocus
                      value={searchText}
                      placeholder="Search seeker with name or mobile number"
                      onChange={handleSearchInputChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                        handleSearch && handleSearch(e.currentTarget.value);
                        }
                      }}
                      data-testid="search-input"
                    />
                    {searchText && (
                      <button
                        className={styles.clearIcon}
                        onClick={() => {
                          setSearchText(""); // Clear the search text
                          handleSearch && handleSearch(""); // Trigger search with empty value
                        }}
                        data-testid="clear-button"
                      >
                        <img
                          src={clearIcon}
                          alt="clear"
                          data-testid="clear-icon"
                        />
                      </button>
                    )}
                  </div>
                )}
                <Tooltip title="Search" arrow>
                  <button className={styles.searchIcon} data-testid="search-button">
                    <img
                      src={search}
                      alt="search"
                      data-testid="search-icon"
                      onClick={() =>
                        setSearchValue({ value: "", open: !searchValue.open })
                      }
                    />
                  </button>
                </Tooltip>
          </div>
      </div>

      <TableWithPaginationComponent
        columns={allSeekersListColumns}
        data={dataToShow}
        loading={filterLoader}
        noDataMessage="No results found"
        currentPage={currentPage}
        pageSize={pageSize}
        total={totalAudience}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        itemsPerPageOptions={getItemsPerPageOptions()}
        showPagination={true}
        itemLabel="seekers"
        renderLoader={renderLoader}
        renderCustomRow={renderCustomRow}
        customRowClassName={styles.tableRow}
        subHeader={false}
      />

    </div>) :  (<Loader type="large" data-test-id = "loader" />)}
    </>
  );
};

export default AllSeekersList;